import { useCallback, useState } from 'react';
import isNil from 'lodash/isNil';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	performPostRequest,
	performPutRequest,
} from '@atlassian/jira-servicedesk-common/src/utils/fetch/requests.tsx';
import type { BaseUrl, ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { MODE_ANALYTICS } from '../../common/types';
import { transformCalendarResponse } from '../../common/utils/calendars/utils';
import type { SaveCalendarRequest, State } from './types';
import { getUrl } from './utils';

export const useSaveCalendarService = (baseUrl: BaseUrl, projectKey: ProjectKey): State => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [data, setData] = useState<unknown>();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<unknown>(null);

	const fetch = useCallback(
		async (saveCalendarRequest: SaveCalendarRequest, calendarMode: MODE_ANALYTICS) => {
			setLoading(true);
			setError(null);
			// existing calendar will use calendar id which is passed down to put api request
			// new calendar doesn't have calendar id app will perform post request for new calendar.
			const asyncRequest = isNil(saveCalendarRequest.id) ? performPostRequest : performPutRequest;

			try {
				const response = await asyncRequest(getUrl(baseUrl, projectKey, saveCalendarRequest.id), {
					body: JSON.stringify(saveCalendarRequest),
				});
				const formattedResponse = transformCalendarResponse(response);
				setData(formattedResponse);
				fireTrackAnalytics(
					createAnalyticsEvent({}),
					`slaCalendar ${isNil(saveCalendarRequest.id) ? 'created' : 'updated'}`,
					{
						calendarMode,
						holidayCount: formattedResponse.holidays.filter((holiday) => !holiday.isRecurring)
							.length,
						recurringHolidayCount: formattedResponse.holidays.filter(
							(holiday) => holiday.isRecurring,
						).length,
					},
				);
				return formattedResponse;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setError(err);
				trackFetchErrors({
					error: err,
					id: 'useSaveCalendarService',
					packageName: 'jiraServicedeskSlaSettings',
					sendToPrivacyUnsafeSplunk: true,
					attributes: {
						calendarMode,
					},
				});
				return undefined;
			} finally {
				setLoading(false);
			}
		},
		[baseUrl, projectKey, createAnalyticsEvent],
	);
	return [
		{
			loading,
			// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'ServiceError | undefined'.
			error,
			// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'Data'.
			data,
		},
		{
			fetch,
		},
	];
};
